/**
* TelemetryContext.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import { isString, setValue } from "@microsoft/1ds-core-js";
import { Application } from "./context/Application";
import { Cloud } from "./context/Cloud";
import { User } from "./context/User";
import { Web } from "./context/Web";
import { OperatingSystem } from "./context/OperatingSystem";
import { IntWeb } from "./context/IntWeb";
import { Utc } from "./context/Utc";
import { Loc } from "./context/Loc";
import { Device } from "./context/Device";
import { Session } from "./context/Session";
import { Trace } from "./context/Trace";
import { AppExtensionKeys, UserExtensionKeys, WebExtensionKeys, SDKExtKeys, OSExtKeys, IntWebExtKeys, UtcExtKeys, LocExtKeys, SessionExtKeys, DeviceExtKeys, TraceExtKeys, CloudExtKeys, } from "./ExtensionKeys";
import { Extensions } from "./Extensions";
import { Sdk } from "./context/Sdk";
import { SessionManager } from "./SessionManager";
// Use a local variable as the reference so it will be minified for the 25ish usages
var _isString = isString;
var TelemetryContext = /** @class */ (function () {
    function TelemetryContext(coreConfig, propertiesConfig, core) {
        var self = this;
        self.app = new Application(propertiesConfig, core);
        self.cloud = new Cloud();
        self.user = new User(coreConfig, propertiesConfig, core);
        self.os = new OperatingSystem(propertiesConfig);
        self.web = new Web(propertiesConfig, core);
        self.sdk = new Sdk(coreConfig, core);
        self.intWeb = new IntWeb(propertiesConfig, core);
        self.utc = new Utc(propertiesConfig);
        self.loc = new Loc();
        self.device = new Device();
        self.telemetryTrace = new Trace(propertiesConfig);
        self.sessionManager = new SessionManager(core, propertiesConfig);
        self.session = new Session();
    }
    /**
     * Returns the current session id, if one does not already exist a new one will be attempted to be created.
     * If you have set a specific custom id via session.setId(), this function will return that value.
     */
    TelemetryContext.prototype.getSessionId = function () {
        var session = this.session;
        if (session && _isString(session.customId)) {
            return session.customId;
        }
        var sessionManager = this.sessionManager;
        sessionManager.update();
        var autoSession = sessionManager.automaticSession;
        if (autoSession) {
            var autoId = autoSession.getId();
            if (autoId && _isString(autoId)) {
                session.automaticId = autoId;
            }
        }
        return session.automaticId;
    };
    TelemetryContext.prototype.applyApplicationContext = function (event) {
        var app = this.app;
        var evtApp = event.ext[Extensions.AppExt];
        setValue(evtApp, AppExtensionKeys.id, app.id, _isString);
        setValue(evtApp, AppExtensionKeys.ver, app.ver, _isString);
        setValue(evtApp, AppExtensionKeys.appName, app.name, _isString);
        setValue(evtApp, AppExtensionKeys.locale, app.locale, _isString);
        setValue(evtApp, AppExtensionKeys.expId, app.getExpId(), _isString);
        setValue(evtApp, AppExtensionKeys.env, app.env, _isString);
    };
    TelemetryContext.prototype.applyUserContext = function (event) {
        var user = this.user;
        var evtUser = event.ext[Extensions.UserExt];
        setValue(evtUser, UserExtensionKeys.localId, user.getLocalId(), _isString);
        setValue(evtUser, UserExtensionKeys.locale, user.locale, _isString);
        setValue(evtUser, UserExtensionKeys.id, user.id, _isString);
    };
    TelemetryContext.prototype.applyWebContext = function (event) {
        var web = this.web;
        var evtWeb = event.ext[Extensions.WebExt];
        setValue(evtWeb, WebExtensionKeys.domain, web.domain, _isString);
        setValue(evtWeb, WebExtensionKeys.browser, web.browser, _isString);
        setValue(evtWeb, WebExtensionKeys.browserVer, web.browserVer, _isString);
        setValue(evtWeb, WebExtensionKeys.screenRes, web.screenRes, _isString);
        evtWeb[WebExtensionKeys.userConsent] = web.getUserConsent();
        evtWeb[WebExtensionKeys.consentDetails] = web.getUserConsentDetails();
    };
    TelemetryContext.prototype.applyOsContext = function (event) {
        var os = this.os;
        var evtOs = event.ext[Extensions.OSExt];
        setValue(evtOs, OSExtKeys.osName, os.name, _isString);
        setValue(evtOs, OSExtKeys.ver, os.ver, _isString);
    };
    TelemetryContext.prototype.applySdkContext = function (event) {
        var sdk = this.sdk;
        var evtSdk = event.ext[Extensions.SdkExt];
        evtSdk[SDKExtKeys.seq] = sdk.getSequenceId();
        evtSdk[SDKExtKeys.epoch] = sdk.epoch;
        setValue(evtSdk, SDKExtKeys.installId, sdk.installId, _isString);
    };
    TelemetryContext.prototype.applyIntWebContext = function (event) {
        var intWeb = this.intWeb;
        var evtIntWeb = event.ext[Extensions.IntWebExt];
        setValue(evtIntWeb, IntWebExtKeys.msfpc, intWeb.getMsfpc(), _isString);
        setValue(evtIntWeb, IntWebExtKeys.anid, intWeb.getAnid(), _isString);
        setValue(evtIntWeb, IntWebExtKeys.serviceName, intWeb.serviceName, _isString);
    };
    TelemetryContext.prototype.applyUtcContext = function (event) {
        var utc = this.utc;
        var evtUtc = event.ext[Extensions.UtcExt];
        evtUtc[UtcExtKeys.popSample] = utc.popSample;
        if (utc.eventFlags > 0) {
            evtUtc[UtcExtKeys.eventFlags] = utc.eventFlags;
        }
    };
    TelemetryContext.prototype.applyLocContext = function (event) {
        event.ext[Extensions.LocExt][LocExtKeys.tz] = this.loc.tz;
    };
    TelemetryContext.prototype.applySessionContext = function (event) {
        event.ext[Extensions.AppExt][SessionExtKeys.sessionId] = this.getSessionId();
    };
    TelemetryContext.prototype.applyDeviceContext = function (event) {
        var device = this.device;
        var evtDevice = event.ext[Extensions.DeviceExt];
        setValue(evtDevice, DeviceExtKeys.localId, device.localId, _isString);
        setValue(evtDevice, DeviceExtKeys.make, device.make, _isString);
        setValue(evtDevice, DeviceExtKeys.model, device.model, _isString);
        setValue(evtDevice, DeviceExtKeys.deviceClass, device.deviceClass, _isString);
    };
    TelemetryContext.prototype.applyCloudContext = function (event) {
        var cloud = this.cloud;
        var evtCloud = event.ext[Extensions.CloudExt];
        setValue(evtCloud, CloudExtKeys.role, cloud.role, _isString);
        setValue(evtCloud, CloudExtKeys.roleInstance, cloud.roleInstance, _isString);
        setValue(evtCloud, CloudExtKeys.roleVer, cloud.roleVer, _isString);
    };
    TelemetryContext.prototype.applyAITraceContext = function (event) {
        var trace = this.telemetryTrace;
        var evtTrace = event.ext[Extensions.TraceExt];
        setValue(evtTrace, TraceExtKeys.traceId, trace.traceId, _isString);
        setValue(evtTrace, TraceExtKeys.traceName, trace.name, _isString);
        setValue(evtTrace, TraceExtKeys.parentId, trace.parentId, _isString);
    };
    return TelemetryContext;
}());
export { TelemetryContext };
//# sourceMappingURL=TelemetryContext.js.map